import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, Trash2, Upload, FileText, Download, BookOpen, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminEbooks = () => {
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const emptyForm = {
    name: '', description: '', price: '', author: '', pages: '',
    category: 'ebook', cover_url: '', file_path: '', file_format: '',
    file_size: 0, original_filename: '', affiliate_url: '', is_featured: false, is_active: true,
  };
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { fetchEbooks(); }, []);

  const fetchEbooks = async () => {
    try {
      const res = await axios.get(`${API}/admin/ebooks`);
      setEbooks(res.data);
    } catch (err) {
      toast.error('Failed to load e-books');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'epub'].includes(ext)) {
      toast.error('Only PDF and EPUB files are allowed');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large. Maximum 50MB.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/ebooks/upload-file`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData(prev => ({
        ...prev,
        file_path: res.data.file_path,
        file_format: res.data.file_format,
        file_size: res.data.file_size,
        original_filename: res.data.original_filename,
      }));
      toast.success(`File uploaded: ${file.name}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await axios.post(`${API}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData(prev => ({ ...prev, cover_url: res.data.url }));
      toast.success('Cover image uploaded');
    } catch (err) {
      toast.error('Cover upload failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.price) {
      toast.error('Name and price are required');
      return;
    }
    if (!editId && !formData.file_path && !formData.affiliate_url) {
      toast.error('Please upload an e-book file or add an affiliate link');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        pages: parseInt(formData.pages) || 0,
      };

      if (editId) {
        await axios.put(`${API}/admin/ebooks/${editId}`, payload);
        toast.success('E-book updated');
      } else {
        await axios.post(`${API}/admin/ebooks`, payload);
        toast.success('E-book created');
      }
      setShowForm(false);
      setEditId(null);
      setFormData(emptyForm);
      fetchEbooks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (eb) => {
    setFormData({
      name: eb.name || '', description: eb.description || '', price: eb.price?.toString() || '',
      author: eb.author || '', pages: eb.pages?.toString() || '', category: eb.category || 'ebook',
      cover_url: eb.cover_url || '', file_path: eb.file_path || '', file_format: eb.file_format || '',
      file_size: eb.file_size || 0, original_filename: eb.original_filename || '',
      affiliate_url: eb.affiliate_url || '',
      is_featured: eb.is_featured || false, is_active: eb.is_active !== false,
    });
    setEditId(eb.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this e-book?')) return;
    try {
      await axios.delete(`${API}/admin/ebooks/${id}`);
      toast.success('E-book deleted');
      fetchEbooks();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-ebooks">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-serif font-bold text-foreground">E-books & Digital Guides</h2>
          <p className="text-muted-foreground">{ebooks.length} digital products</p>
        </div>
        <Button onClick={() => { setShowForm(true); setEditId(null); setFormData(emptyForm); }} className="btn-accent" data-testid="add-ebook-btn">
          <Plus className="h-4 w-4 mr-2" />Add E-book
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="ebook-form">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-serif">{editId ? 'Edit' : 'Add'} E-book</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}><X className="h-4 w-4" /></Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Title *</Label>
                    <Input value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))} placeholder="E-book title" data-testid="ebook-name" />
                  </div>
                  <div>
                    <Label>Author</Label>
                    <Input value={formData.author} onChange={e => setFormData(p => ({...p, author: e.target.value}))} placeholder="Author name" data-testid="ebook-author" />
                  </div>
                  <div>
                    <Label>Price ($) *</Label>
                    <Input type="number" step="0.01" min="0" value={formData.price} onChange={e => setFormData(p => ({...p, price: e.target.value}))} placeholder="9.99" data-testid="ebook-price" />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <select value={formData.category} onChange={e => setFormData(p => ({...p, category: e.target.value}))} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="ebook-category">
                      <option value="ebook">E-book</option>
                      <option value="guide">Digital Guide</option>
                      <option value="workbook">Workbook</option>
                      <option value="journal">Journal</option>
                    </select>
                  </div>
                  <div>
                    <Label>Pages</Label>
                    <Input type="number" min="0" value={formData.pages} onChange={e => setFormData(p => ({...p, pages: e.target.value}))} placeholder="200" data-testid="ebook-pages" />
                  </div>
                </div>

                <div>
                  <Label>Description</Label>
                  <textarea value={formData.description} onChange={e => setFormData(p => ({...p, description: e.target.value}))} placeholder="What's this e-book about?" rows={3} className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="ebook-description" />
                </div>

                {/* File Upload */}
                <div>
                  <Label>E-book File (PDF / EPUB) *</Label>
                  <input type="file" ref={fileInputRef} accept=".pdf,.epub" onChange={handleFileUpload} className="hidden" />
                  <div className="mt-1">
                    {formData.file_path ? (
                      <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                        <FileText className="h-5 w-5 text-primary" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{formData.original_filename || 'File uploaded'}</p>
                          <p className="text-xs text-muted-foreground">{formData.file_format?.toUpperCase()} - {formatSize(formData.file_size)}</p>
                        </div>
                        <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>Replace</Button>
                      </div>
                    ) : (
                      <Button type="button" variant="outline" className="w-full h-20 border-dashed" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-ebook-file">
                        {uploading ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Upload className="h-5 w-5 mr-2" />}
                        {uploading ? 'Uploading...' : 'Upload PDF or EPUB'}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Cover Image */}
                <div>
                  <Label>Cover Image</Label>
                  <input type="file" ref={coverInputRef} accept="image/*" onChange={handleCoverUpload} className="hidden" />
                  <div className="flex items-center gap-3 mt-1">
                    {formData.cover_url ? (
                      <img src={formData.cover_url} alt="Cover" className="h-20 w-14 object-cover rounded border" />
                    ) : (
                      <div className="h-20 w-14 bg-muted rounded border flex items-center justify-center">
                        <BookOpen className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1">
                      <Input value={formData.cover_url} onChange={e => setFormData(p => ({...p, cover_url: e.target.value}))} placeholder="Cover image URL" data-testid="ebook-cover-url" />
                      <Button type="button" variant="link" size="sm" className="px-0 mt-1" onClick={() => coverInputRef.current?.click()}>
                        Or upload image
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Toggles */}
                <div className="space-y-3">
                  <div>
                    <Label>Affiliate Link (optional)</Label>
                    <Input value={formData.affiliate_url} onChange={e => setFormData(p => ({...p, affiliate_url: e.target.value}))} placeholder="https://amazon.com/... or any partner link" data-testid="ebook-affiliate-url" />
                    <p className="text-xs text-muted-foreground mt-1">External purchase link shown alongside Add to Cart</p>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <Switch checked={formData.is_featured} onCheckedChange={v => setFormData(p => ({...p, is_featured: v}))} data-testid="ebook-featured" />
                      <Label>Featured</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={formData.is_active} onCheckedChange={v => setFormData(p => ({...p, is_active: v}))} data-testid="ebook-active" />
                      <Label>Active</Label>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="submit" className="btn-accent flex-1" disabled={saving} data-testid="ebook-submit">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    {editId ? 'Update' : 'Create'} E-book
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* E-books Grid */}
      {ebooks.length === 0 ? (
        <div className="text-center py-20">
          <BookOpen className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
          <h3 className="font-serif text-xl font-bold mb-2">No e-books yet</h3>
          <p className="text-muted-foreground mb-4">Start adding e-books and digital guides to sell.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {ebooks.map(eb => (
            <Card key={eb.id} className={`overflow-hidden ${!eb.is_active ? 'opacity-60' : ''}`} data-testid={`ebook-card-${eb.id}`}>
              <div className="flex">
                <div className="w-24 h-32 bg-muted flex-shrink-0 flex items-center justify-center overflow-hidden">
                  {eb.cover_url ? (
                    <img src={eb.cover_url} alt={eb.name} className="w-full h-full object-cover" />
                  ) : (
                    <BookOpen className="h-8 w-8 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 p-3 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-sm truncate">{eb.name}</h3>
                      {eb.author && <p className="text-xs text-muted-foreground">by {eb.author}</p>}
                    </div>
                    <span className="font-bold text-primary text-sm flex-shrink-0">${eb.price?.toFixed(2)}</span>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    <Badge variant="outline" className="text-xs">{eb.file_format?.toUpperCase()}</Badge>
                    <Badge variant="outline" className="text-xs">{eb.category}</Badge>
                    {eb.is_featured && <Badge className="text-xs bg-amber-500">Featured</Badge>}
                    {eb.affiliate_url && <Badge className="text-xs bg-blue-600 text-white">Affiliate</Badge>}
                    {!eb.is_active && <Badge variant="destructive" className="text-xs">Inactive</Badge>}
                  </div>
                  <div className="flex items-center gap-1 mt-2">
                    <Download className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">{eb.downloads || 0} downloads</span>
                    <span className="text-xs text-muted-foreground ml-2">{formatSize(eb.file_size)}</span>
                  </div>
                  <div className="flex gap-1 mt-2">
                    <Button variant="ghost" size="sm" onClick={() => startEdit(eb)} data-testid={`edit-ebook-${eb.id}`}><Pencil className="h-3 w-3" /></Button>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(eb.id)} data-testid={`delete-ebook-${eb.id}`}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminEbooks;
